import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Package } from "lucide-react";
import config from "../../../config";

const ProductDetails = () => {
  const { id: userId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(
          `${config.baseURL}/orderdproduct/${userId}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const productData = await response.json();

        if (!productData) {
          setError("No product data received");
          setOrder(null);
          return;
        }

        // Process the product data into a standardized format
        const product = productData[0]; // Access first product object
        const formattedOrder = [
          { label: "Name", value: product.name },
          { label: "Bore", value: product.bore },
          { label: "Thick", value: product.thick },
          { label: "Thickness Tolerance", value: product.thickness_tolerance },
          { label: "Free Diameter", value: product.free_diameter },
          { label: "clearance Onbore", value: product.clearance_onbore },
          { label: "Beam", value: product.beam },
          { label: "Lug", value: product.lug },
          { label: "Hole Diamter", value: product.hole_diamter },
          {
            label: "Ring Load Carrying Capacity",
            value: product.ring_load_carrying_capacity,
          },
          { label: "Groove Diameter", value: product.groove_diameter },
          { label: "Groove Width", value: product.groove_width },
          { label: "Edge Margin", value: product.edge_margin },
          {
            label: "Groove Load Capacity",
            value: product.groove_load_capacity,
          },
          { label: "Price 1 to 249", value: product.price_1_249 },
        ];

        setOrder(formattedOrder);
      } catch (err) {
        console.error("Error fetching product details:", err);
        setError(
          "Failed to load product details. Please check the network and try again."
        );
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchOrderDetails();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100"></div>
        <p className="mt-4 text-lg text-gray-700 dark:text-gray-300">
          Loading product details...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 text-red-600 dark:text-red-400">
        <p className="text-xl">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <p className="text-xl text-gray-700 dark:text-gray-300">
          Product not found. Please check the product ID.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-6 p-6 bg-gray-50 dark:bg-gray-800 min-h-screen">
      <h1 className="title text-3xl font-bold text-gray-800 dark:text-white mb-6">
        Product Details
      </h1>

      <div className="card bg-white dark:bg-gray-900 shadow-lg rounded-xl overflow-hidden">
        <div className="card-header p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-blue-50 dark:bg-blue-950">
          <p className="card-title text-xl font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-2">
            <Package size={24} /> Product
          </p>
        </div>

        <div className="card-body p-6">
          {/* Product Specifications */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
              Product Details
            </h2>
            {order.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto border border-gray-300 dark:border-gray-700">
                  <thead>
                    <tr className="bg-gray-200 dark:bg-gray-800 text-left">
                      <th className="px-4 py-2 border-b dark:border-gray-700 text-gray-800 dark:text-white">
                        Label
                      </th>
                      <th className="px-4 py-2 border-b dark:border-gray-700 text-gray-800 dark:text-white">
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.map((spec, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <td className="px-4 py-2 border-b dark:border-gray-700 text-gray-700 dark:text-gray-300">
                          {spec.label}
                        </td>
                        <td className="px-4 py-2 border-b dark:border-gray-700 text-gray-900 dark:text-white">
                          {spec.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">
                No specifications available
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
